import type { NarrativeArc as AlbumNarrativeArc } from '@/types/coherence.types';
import type { NarrativeArc } from '@/types/narrative.types';

// ============================================================================
// Song-Level Narrative Arcs (Narrative Engine)
// Emotional trajectories for individual songs with genre affinity
// ============================================================================

export const NARRATIVE_ARCS: NarrativeArc[] = [
  {
    id: 'slow-burn',
    label: 'Slow Burn',
    description: 'Restrained \u2192 simmering \u2192 erupts late',
    shape: 'gradual-rise',
    genreAffinity: ['muscle-shoals', 'stax', 'southern-rock-porch', 'bakersfield', 'americana'],
    promptModifier: `[Arc: Slow Burn]\nEmotional trajectory: restrained opening that masks deeper tension. Each verse turns the heat up incrementally. The chorus doesn't explode until the final pass. Resolution \u2014 if it comes \u2014 is earned through patience, not force.`,
  },
  {
    id: 'confessional-spiral',
    label: 'Confessional Spiral',
    description: 'Intimate \u2192 deeper \u2192 raw exposure \u2192 quiet resolve',
    shape: 'deepening',
    genreAffinity: ['nashville', 'philly', 'muscle-shoals', 'americana'],
    promptModifier: `[Arc: Confessional Spiral]\nEmotional trajectory: begins as a casual admission, then each section peels back another layer of honesty. By the bridge, the narrator is fully exposed. The final chorus is the same words but now they mean everything.`,
  },
  {
    id: 'defiant-rise',
    label: 'Defiant Rise',
    description: 'Down and out \u2192 sparks \u2192 full blaze',
    shape: 'ascent',
    genreAffinity: ['southern-metal', 'southern-rock-porch', 'country-saloon', 'capricorn'],
    promptModifier: `[Arc: Defiant Rise]\nEmotional trajectory: opens from a low point \u2014 beaten, dismissed, counted out. A spark of defiance ignites mid-song. By the final chorus, the narrator is standing in flames, refusing to kneel. Energy only goes up.`,
  },
  {
    id: 'descent',
    label: 'Descent',
    description: 'High ground \u2192 cracks show \u2192 bottoms out',
    shape: 'falling',
    genreAffinity: ['doom-metal', 'southern-metal', 'country-saloon'],
    promptModifier: `[Arc: Descent]\nEmotional trajectory: opens with false confidence or stability. Each verse reveals another crack. The bridge is the point of no return. The final section is the bottom \u2014 no redemption, no lesson, just the weight of what happened.`,
  },
  {
    id: 'reckoning',
    label: 'Reckoning',
    description: 'Tension \u2192 confrontation \u2192 aftermath',
    shape: 'buildup-release',
    genreAffinity: ['southern-rock-porch', 'southern-metal', 'muscle-shoals', 'capricorn'],
    promptModifier: `[Arc: Reckoning]\nEmotional trajectory: tension coils through the verses \u2014 something is coming and the narrator knows it. The chorus is the confrontation itself. The bridge or final section deals with what's left after the dust settles.`,
  },
  {
    id: 'bittersweet-loop',
    label: 'Bittersweet Loop',
    description: 'Hope \u2192 loss \u2192 acceptance \u2192 hope again (circular)',
    shape: 'circular',
    genreAffinity: ['americana', 'nashville', 'bakersfield', 'philly', 'motown'],
    promptModifier: `[Arc: Bittersweet Loop]\nEmotional trajectory: opens with genuine hope or tenderness. Loss arrives by verse two. The bridge is acceptance \u2014 not defeat, just understanding. The final chorus circles back to the opening sentiment, but now it carries the weight of everything in between.`,
  },
  {
    id: 'tall-tale',
    label: 'Tall Tale',
    description: 'Setup \u2192 escalation \u2192 absurd peak \u2192 wink',
    shape: 'escalation',
    genreAffinity: ['country-saloon', 'flyte-time', 'capricorn', 'bakersfield'],
    promptModifier: `[Arc: Tall Tale]\nEmotional trajectory: opens like a credible story \u2014 just colorful enough to hook. Each verse escalates the stakes or absurdity. The bridge goes over the top. The final line or chorus undercuts everything with humor, irony, or a knowing wink.`,
  },
  {
    id: 'resurrection',
    label: 'Resurrection',
    description: 'Dead/broken \u2192 stillness \u2192 first breath \u2192 full return',
    shape: 'valley-peak',
    genreAffinity: ['stax', 'philly', 'motown', 'southern-metal', 'doom-metal'],
    promptModifier: `[Arc: Resurrection]\nEmotional trajectory: opens in the aftermath of destruction \u2014 the narrator is already down. A moment of absolute stillness in the middle (bridge or stripped section). Then life returns \u2014 slowly at first, then with unstoppable force. The final chorus is triumphant but scarred.`,
  },
];

export function getNarrativeArcById(id: string): NarrativeArc | undefined {
  return NARRATIVE_ARCS.find(a => a.id === id);
}

// ============================================================================
// Album-Level Narrative Arcs (Album Mode Coherence System)
// Multi-stage arcs for full album sequencing with mood/key progressions
// ============================================================================

export const ALBUM_NARRATIVE_ARCS: AlbumNarrativeArc[] = [
  {
    id: 'heros-journey',
    name: "Hero's Journey",
    description: "Classic transformation arc: ordinary world \u2192 call \u2192 trials \u2192 transformation \u2192 return changed",
    suggestedTrackCount: [8, 12],
    stages: [
      {
        name: 'The Ordinary World',
        position: 1,
        percentOfAlbum: [0, 12],
        energyTarget: 5,
        themeEmphasis: ['isolation', 'hope', 'homecoming'],
        lyricalGuidance: 'Establish the status quo, hint at restlessness'
      },
      {
        name: 'The Call',
        position: 2,
        percentOfAlbum: [12, 25],
        energyTarget: 6,
        themeEmphasis: ['journey', 'defiance', 'temptation'],
        lyricalGuidance: 'Something disrupts, demands a choice'
      },
      {
        name: 'Crossing the Threshold',
        position: 3,
        percentOfAlbum: [25, 35],
        energyTarget: 7,
        themeEmphasis: ['journey', 'transformation', 'sacrifice'],
        lyricalGuidance: 'Point of no return, leaving the old behind'
      },
      {
        name: 'Trials & Allies',
        position: 4,
        percentOfAlbum: [35, 50],
        energyTarget: 6,
        themeEmphasis: ['betrayal', 'faith', 'love'],
        lyricalGuidance: 'Tests, friends found, enemies revealed'
      },
      {
        name: 'The Innermost Cave',
        position: 5,
        percentOfAlbum: [50, 62],
        energyTarget: 4,
        themeEmphasis: ['despair', 'death', 'loss'],
        lyricalGuidance: 'Darkest moment, facing deepest fear'
      },
      {
        name: 'The Ordeal',
        position: 6,
        percentOfAlbum: [62, 75],
        energyTarget: 9,
        themeEmphasis: ['death', 'transformation', 'sacrifice'],
        lyricalGuidance: 'Death and rebirth, the climactic battle'
      },
      {
        name: 'The Reward',
        position: 7,
        percentOfAlbum: [75, 85],
        energyTarget: 7,
        themeEmphasis: ['redemption', 'hope', 'justice'],
        lyricalGuidance: 'Seizing what was sought, but cost is felt'
      },
      {
        name: 'The Return',
        position: 8,
        percentOfAlbum: [85, 100],
        energyTarget: 8,
        themeEmphasis: ['homecoming', 'transformation', 'legacy'],
        lyricalGuidance: 'Changed forever, bringing back wisdom'
      }
    ],
    moodProgression: ['restless', 'urgent', 'determined', 'embattled', 'despairing', 'triumphant', 'reflective', 'resolute'],
    keyProgression: ['tonic', 'relative', 'subdominant', 'dominant', 'chromatic-down', 'tonic', 'parallel', 'resolution']
  },

  {
    id: 'descent-and-return',
    name: 'Descent & Return',
    description: "Journey into darkness and back: fall \u2192 bottom \u2192 climb \u2192 emerge transformed",
    suggestedTrackCount: [7, 10],
    stages: [
      {
        name: 'Before the Fall',
        position: 1,
        percentOfAlbum: [0, 15],
        energyTarget: 6,
        themeEmphasis: ['faith', 'love', 'hope'],
        lyricalGuidance: 'What was good before it broke'
      },
      {
        name: 'The Fall',
        position: 2,
        percentOfAlbum: [15, 30],
        energyTarget: 7,
        themeEmphasis: ['betrayal', 'loss', 'despair'],
        lyricalGuidance: 'The moment everything collapsed'
      },
      {
        name: 'The Descent',
        position: 3,
        percentOfAlbum: [30, 45],
        energyTarget: 5,
        themeEmphasis: ['isolation', 'despair', 'death'],
        lyricalGuidance: 'Spiraling deeper, losing grip'
      },
      {
        name: 'Rock Bottom',
        position: 4,
        percentOfAlbum: [45, 55],
        energyTarget: 3,
        themeEmphasis: ['death', 'despair', 'sacrifice'],
        lyricalGuidance: 'The lowest point, surrender or stand'
      },
      {
        name: 'The Spark',
        position: 5,
        percentOfAlbum: [55, 65],
        energyTarget: 5,
        themeEmphasis: ['hope', 'defiance', 'faith'],
        lyricalGuidance: 'Something ignites, refuses to die'
      },
      {
        name: 'The Climb',
        position: 6,
        percentOfAlbum: [65, 80],
        energyTarget: 7,
        themeEmphasis: ['redemption', 'journey', 'transformation'],
        lyricalGuidance: 'Clawing back, earning each step'
      },
      {
        name: 'Emergence',
        position: 7,
        percentOfAlbum: [80, 100],
        energyTarget: 9,
        themeEmphasis: ['redemption', 'transformation', 'freedom'],
        lyricalGuidance: 'Reborn, scarred but standing'
      }
    ],
    moodProgression: ['content', 'shattered', 'sinking', 'hollow', 'defiant', 'determined', 'triumphant'],
    keyProgression: ['tonic', 'relative', 'chromatic-down', 'subdominant', 'tonic', 'dominant', 'resolution']
  },

  {
    id: 'revenge-arc',
    name: 'The Revenge Arc',
    description: "Wrong done \u2192 hunt \u2192 confrontation \u2192 choice between vengeance and release",
    suggestedTrackCount: [7, 9],
    stages: [
      {
        name: 'The Wrong',
        position: 1,
        percentOfAlbum: [0, 15],
        energyTarget: 7,
        themeEmphasis: ['betrayal', 'loss', 'justice'],
        lyricalGuidance: 'The injury that demands answer'
      },
      {
        name: 'The Vow',
        position: 2,
        percentOfAlbum: [15, 30],
        energyTarget: 8,
        themeEmphasis: ['revenge', 'defiance', 'death'],
        lyricalGuidance: 'The promise of reckoning'
      },
      {
        name: 'The Hunt',
        position: 3,
        percentOfAlbum: [30, 50],
        energyTarget: 7,
        themeEmphasis: ['journey', 'sacrifice', 'isolation'],
        lyricalGuidance: 'Pursuit consumes everything else'
      },
      {
        name: 'The Cost',
        position: 4,
        percentOfAlbum: [50, 65],
        energyTarget: 5,
        themeEmphasis: ['loss', 'isolation', 'despair'],
        lyricalGuidance: 'What revenge has taken from you'
      },
      {
        name: 'The Confrontation',
        position: 5,
        percentOfAlbum: [65, 80],
        energyTarget: 10,
        themeEmphasis: ['revenge', 'justice', 'death'],
        lyricalGuidance: 'Face to face with the enemy'
      },
      {
        name: 'The Choice',
        position: 6,
        percentOfAlbum: [80, 100],
        energyTarget: 6,
        themeEmphasis: ['redemption', 'freedom', 'transformation'],
        lyricalGuidance: 'Kill or release? And what does it cost?'
      }
    ],
    moodProgression: ['wounded', 'seething', 'relentless', 'hollow', 'explosive', 'contemplative'],
    keyProgression: ['tonic', 'dominant', 'relative', 'chromatic-down', 'tonic', 'resolution']
  },

  {
    id: 'love-and-loss',
    name: 'Love & Loss',
    description: "A relationship's full arc: the spark, the fall, the fracture, and what's left standing after",
    suggestedTrackCount: [7, 10],
    stages: [
      {
        name: 'First Spark',
        position: 1,
        percentOfAlbum: [0, 15],
        energyTarget: 4,
        themeEmphasis: ['love', 'wonder', 'hope'],
        lyricalGuidance: 'The first pull toward someone, before the stakes are known'
      },
      {
        name: 'Falling In',
        position: 2,
        percentOfAlbum: [15, 30],
        energyTarget: 6,
        themeEmphasis: ['love', 'faith', 'homecoming'],
        lyricalGuidance: 'Deep in it now — devotion, building a life together'
      },
      {
        name: 'Cracks Show',
        position: 3,
        percentOfAlbum: [30, 45],
        energyTarget: 5,
        themeEmphasis: ['temptation', 'guilt', 'isolation'],
        lyricalGuidance: 'The first sign something is wrong, still hoping it passes'
      },
      {
        name: 'The Break',
        position: 4,
        percentOfAlbum: [45, 60],
        energyTarget: 8,
        themeEmphasis: ['betrayal', 'loss', 'grief'],
        lyricalGuidance: 'The moment it ends — the fight, the leaving, or the discovery'
      },
      {
        name: 'The Aftermath',
        position: 5,
        percentOfAlbum: [60, 80],
        energyTarget: 4,
        themeEmphasis: ['grief', 'isolation', 'nostalgia'],
        lyricalGuidance: 'Living in the empty space they left behind'
      },
      {
        name: 'What Remains',
        position: 6,
        percentOfAlbum: [80, 100],
        energyTarget: 6,
        themeEmphasis: ['hope', 'legacy', 'redemption'],
        lyricalGuidance: 'Not fully healed, but able to name what it meant'
      }
    ],
    moodProgression: ['tender', 'devoted', 'uneasy', 'shattered', 'hollow', 'settled'],
    keyProgression: ['tonic', 'relative', 'subdominant', 'parallel', 'chromatic-down', 'resolution']
  },

  {
    id: 'rebellion',
    name: 'Rebellion',
    description: 'Systemic weight to open uprising: oppression, the first spark of defiance, and what it costs to rise',
    suggestedTrackCount: [7, 9],
    stages: [
      {
        name: 'Under the Boot',
        position: 1,
        percentOfAlbum: [0, 15],
        energyTarget: 4,
        themeEmphasis: ['oppression', 'isolation', 'despair'],
        lyricalGuidance: 'Establish the weight being pushed against — the rules, the silence demanded'
      },
      {
        name: 'The Spark',
        position: 2,
        percentOfAlbum: [15, 30],
        energyTarget: 6,
        themeEmphasis: ['defiance', 'hope', 'pride'],
        lyricalGuidance: 'The first refusal, small but real'
      },
      {
        name: 'Gathering',
        position: 3,
        percentOfAlbum: [30, 45],
        energyTarget: 6,
        themeEmphasis: ['freedom', 'faith', 'journey'],
        lyricalGuidance: 'Others join — no longer a lone voice'
      },
      {
        name: 'Rising Up',
        position: 4,
        percentOfAlbum: [45, 65],
        energyTarget: 9,
        themeEmphasis: ['rebellion', 'defiance', 'justice'],
        lyricalGuidance: 'The uprising itself, full force, no turning back'
      },
      {
        name: 'The Cost',
        position: 5,
        percentOfAlbum: [65, 80],
        energyTarget: 5,
        themeEmphasis: ['sacrifice', 'grief', 'loss'],
        lyricalGuidance: 'What rising up took from the people who did it'
      },
      {
        name: 'New Dawn',
        position: 6,
        percentOfAlbum: [80, 100],
        energyTarget: 8,
        themeEmphasis: ['freedom', 'hope', 'legacy'],
        lyricalGuidance: 'Standing in whatever comes after — changed, not guaranteed easy'
      }
    ],
    moodProgression: ['crushed', 'defiant', 'united', 'explosive', 'grieving', 'resolute'],
    keyProgression: ['tonic', 'chromatic-up', 'relative', 'dominant', 'chromatic-down', 'resolution']
  },

  {
    id: 'coming-of-age',
    name: 'Coming of Age',
    description: 'Innocence to adulthood: the first cracks in a sheltered world, the mistakes, the leaving, and who you become',
    suggestedTrackCount: [8, 10],
    stages: [
      {
        name: 'Still a Kid',
        position: 1,
        percentOfAlbum: [0, 12],
        energyTarget: 4,
        themeEmphasis: ['wonder', 'nostalgia', 'homecoming'],
        lyricalGuidance: 'The sheltered world before it starts to crack'
      },
      {
        name: 'First Cracks',
        position: 2,
        percentOfAlbum: [12, 28],
        energyTarget: 5,
        themeEmphasis: ['temptation', 'guilt', 'loss'],
        lyricalGuidance: 'The world turns out to be bigger and rougher than promised'
      },
      {
        name: 'Learning the Hard Way',
        position: 3,
        percentOfAlbum: [28, 45],
        energyTarget: 6,
        themeEmphasis: ['guilt', 'betrayal', 'endurance'],
        lyricalGuidance: 'Real mistakes with real consequences, no one to bail you out'
      },
      {
        name: 'Breaking Away',
        position: 4,
        percentOfAlbum: [45, 60],
        energyTarget: 7,
        themeEmphasis: ['defiance', 'freedom', 'journey'],
        lyricalGuidance: 'Leaving what raised you, for better or worse'
      },
      {
        name: 'On My Own',
        position: 5,
        percentOfAlbum: [60, 80],
        energyTarget: 6,
        themeEmphasis: ['isolation', 'pride', 'endurance'],
        lyricalGuidance: 'Standing without a safety net, and staying standing'
      },
      {
        name: "Who I've Become",
        position: 6,
        percentOfAlbum: [80, 100],
        energyTarget: 7,
        themeEmphasis: ['transformation', 'legacy', 'pride'],
        lyricalGuidance: 'Looking back at who you were with clear, earned eyes'
      }
    ],
    moodProgression: ['innocent', 'unsettled', 'humbled', 'restless', 'hardened', 'grounded'],
    keyProgression: ['tonic', 'subdominant', 'chromatic-down', 'dominant', 'relative', 'resolution']
  }
];

/** @deprecated Use getAlbumNarrativeArcById — kept for backward compatibility */
export function getNarrativeArc(id: string): AlbumNarrativeArc | undefined {
  return ALBUM_NARRATIVE_ARCS.find(arc => arc.id === id);
}

export function getAlbumNarrativeArcById(id: string): AlbumNarrativeArc | undefined {
  return ALBUM_NARRATIVE_ARCS.find(arc => arc.id === id);
}

export function getNarrativeArcOptions(): { id: string; name: string; description: string }[] {
  return ALBUM_NARRATIVE_ARCS.map(({ id, name, description }) => ({ id, name, description }));
}
